import { memo, useMemo } from 'react';
import { Autocomplete, TextField, Checkbox, FormControlLabel, Box } from '@mui/material';
import { FieldValue } from '../../../../../types/QueryBuilder';
import { useLanguage } from '../../../../../context/LanguageContext';
import CheckboxListPicker from './CheckboxListPicker';
import { formatSelectedCount } from './selectedCountLabel';

interface SelectEditorProps {
    ruleId: string;
    value: string | string[];
    values: FieldValue[];
    isMultiSelect?: boolean;
    // Renders a plain checkbox list with no search field, "select all", or scroll container —
    // for fields with a handful of options where a dropdown-like box only adds friction.
    compact?: boolean;
    onChange: (value: string | string[]) => void;
}

const SelectEditor = memo(function SelectEditor({ ruleId, value, values, isMultiSelect = false, compact = false, onChange }: SelectEditorProps) {
    const { t } = useLanguage();
    const selectedValues = useMemo(() => (Array.isArray(value) ? value : []), [value]);
    const selectedSet = useMemo(() => new Set(selectedValues), [selectedValues]);

    if (isMultiSelect) {
        const toggleOption = (optionName: string, checked: boolean) => {
            const newValues = checked
                ? [...selectedValues, optionName]
                : selectedValues.filter(v => v !== optionName);
            onChange(newValues);
        };

        if (compact) {
            return (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 1 }}>
                    {values.map((option, idx) => (
                        <FormControlLabel
                            key={`${ruleId}-compact-${idx}`}
                            sx={{ m: 0 }}
                            control={
                                <Checkbox
                                    checked={selectedSet.has(option.name)}
                                    onChange={(e) => toggleOption(option.name, e.target.checked)}
                                    size="small"
                                />
                            }
                            label={option.label}
                        />
                    ))}
                </Box>
            );
        }

        return (
            <CheckboxListPicker
                values={values}
                selectedValues={selectedValues}
                onToggleOption={toggleOption}
                onToggleSelectAll={(allSelected) => onChange(allSelected ? [] : values.map(option => option.name))}
                searchPlaceholder={t('pages.clients.queryBuilder.searchPlaceholder')}
                selectAllLabel={t('pages.clients.queryBuilder.simple.selectAll')}
                noOptionsLabel={t('pages.clients.queryBuilder.simple.noOptionsFound')}
                selectedCountLabel={(count) => formatSelectedCount(t, count)}
            />
        );
    }

    const selectedOption = values.find(option => option.name === value) || null;

    return (
        <Autocomplete
            fullWidth
            size="small"
            id={`${ruleId}-select`}
            options={values}
            value={selectedOption}
            onChange={(_e, newValue) => onChange(newValue ? newValue.name : '')}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, val) => option.name === val.name}
            renderInput={(params) => (
                <TextField
                    {...params}
                    placeholder={t('pages.clients.queryBuilder.selectPlaceholder')}
                    inputProps={{ ...params.inputProps, autoComplete: 'off' }}
                />
            )}
            ListboxProps={{ style: { maxHeight: 300 } }}
            noOptionsText={t('pages.clients.queryBuilder.simple.noOptionsFound')}
        />
    );
});

export default SelectEditor;
